/**
 * Feature flag keys used throughout the application.
 *
 * This enum provides strongly-typed access to feature flags.
 * All flag values use the pattern: `ff_sp_*` where `sp` indicates Seller Portal marketplace.
 *
 * @example
 * ```typescript
 * import { useFeatureFlag } from '@/hooks/useFeatureFlag';
 * import { FeatureFlagKeys } from '@/types/featureFlags';
 *
 * const hasNewLotMetadata = useFeatureFlag(FeatureFlagKeys.NEW_LOT_METADATA);
 * ```
 */
export enum FeatureFlagKeys {
    /**
     * Feature flag for AI Lot Creation Assistant
     * Gates the AI-powered vision analysis and pricing research panel on the Create Lot page
     * API Key: ff_sp_ai-lot-assistant
     */
    AI_LOT_ASSISTANT = 'ff_sp_ai_lot_powered_create',

    /**
     * Feature flag for linked auction groups (SP2-5208 / SP2-5212 / SP2-5700).
     * Visibility only. ON shows linking surfaces and those surfaces call live auction-links APIs.
     * OFF hides the surfaces, including a not-found response for /auction/linked-auctions.
     * Data hooks do not read this flag. Reload after Secret Menu changes. API Key: ff_sp_link_auctions
     */
    LINK_AUCTIONS = 'ff_sp_link_auctions',

    /**
     * Feature flag for drag-and-drop image upload on Manage Photos lot cards
     * Gates the FileDropzoneOverlay that wraps each LotCard body
     * API Key: ff_sp_manage-photos-drop-upload
     */
    MANAGE_PHOTOS_DROP_UPLOAD = 'ff_sp_manage-photos-drop-upload',

    /**
     * Feature flag for auction category relocation
     * Moves category from auctionDetails.universalCategory to root-level localCategoryId
     * API Key: ff_sp_local-category-relocation
     */
    LOCAL_CATEGORY_RELOCATION = 'ff_sp_local-category-relocation',

    /**
     * Feature flag for new lot metadata functionality
     * API Key: ff_sp_new-lot-metadata
     */
    NEW_LOT_METADATA = 'ff_sp_new-lot-metadata',

    /**
     * Feature flag for company profile fax number field
     * Gates the fax input on the Company Profile contact details form
     * API Key: ff_sp_company-profile-fax
     */
    COMPANY_PROFILE_FAX = 'ff_sp_company-profile-fax',

    /**
     * Feature flag for company profile invoice email field
     * Gates the invoice email input on the Company Profile contact details form
     * API Key: ff_sp_company-profile-invoice-email
     */
    COMPANY_PROFILE_INVOICE_EMAIL = 'ff_sp_company-profile-invoice-email',

    /**
     * Feature flag for company profile more info email field
     * Gates the more info email input on the Company Profile contact details form
     * API Key: ff_sp_company-profile-more-info-email
     */
    COMPANY_PROFILE_MORE_INFO_EMAIL = 'ff_sp_company-profile-more-info-email',

    /**
     * Feature flag for company profile registration email field
     * Gates the registration email input on the Company Profile contact details form
     * API Key: ff_sp_company-profile-registration-email
     */
    COMPANY_PROFILE_REGISTRATION_EMAIL = 'ff_sp_company-profile-registration-email',

    /**
     * Feature flag for Sales Tax "tax BP at a different rate" fields in the Add/Edit Fee drawer
     * (SP2-5532, epic SP2-5516). Gates the house/auction-only BP tax split section in
     * `AddFeeForm` / `SalesTaxBpRateSection`. Payload fields are FE-scaffolded until SP2-5529 ships.
     * OFF (default) = hide the section. Reload after Secret Menu changes.
     * API Key: ff_sp-additional-fee-details
     */
    ADDITIONAL_FEE_DETAILS = 'ff_sp-additional-fee-details',

    /**
     * Feature flag for Phase 1A cross-listing basics (epic SP2-5022 / SP2-5337).
     * Gates Marketplace section visibility on auction create, edit, and template flows,
     * and empty-destination marketplace defaults in response→form transforms.
     * Off by default in `DEFAULT_FEATURE_FLAGS`; enable per environment via Feature Flag Bridge /
     * Secret Menu / `LOCAL_FEATURE_FLAGS`. FE8 (SP2-5344) does not flip the code default.
     * Later phases use separate keys (`CROSS_LISTING_PARTNER_NETWORK`, `CROSS_LISTING_LOT_MOVE_COPY`).
     * API Key: ff_sp_cross-listing-basics
     */
    CROSS_LISTING_BASICS = 'ff_sp_cross-listing-basics',

    /**
     * Feature flag for Phase 3A Partner Network seller destination selection (SP2-5436 / epic SP2-5400).
     * Gates Partner Network marketplace toggles on create flows; XL marketplace rows remain under
     * `CROSS_LISTING_BASICS`. Off by default until admin enablement and catalog codes land.
     * API Key: ff_sp_cross-listing-partner-network
     */
    CROSS_LISTING_PARTNER_NETWORK = 'ff_sp_cross-listing-partner-network',

    /**
     * Feature flag for Phase 3B Lot Move/Copy UI (SP2-5444 / epic SP2-5401).
     * Will gate the cross-auction Lot Move/Copy UI surfaces once they land (SP2-5440/5441/5442),
     * independent of the per-house admin toggle. OFF (default) = no consuming UI yet — this flag
     * is scaffolding ahead of the Move/Copy stories. Reload after Secret Menu changes.
     * NOTE: distinct from the separate, unrelated Cross-Listing epic (SP2-5400) — do not confuse
     * with a `CROSS_LISTING_LOT_MOVE_COPY`-style name.
     * API Key: ff_sp_lot_copy_move
     */
    LOT_COPY_MOVE = 'ff_sp_lot_copy_move',

    /**
     * Feature flag for the Bulk Lot Edit "Edit fields" entry point (WBPR-4422, epic WBPR-4276).
     * Gates the Edit icon on the Bulk Actions page toolbar. Off by default — Save cannot submit
     * until the WBPR-4426 data-layer hook lands, so the entry point stays hidden until the epic
     * completes.
     * API Key: ff_sp_bulk-lot-edit
     */
    BULK_LOT_EDIT = 'ff_sp_bulk-lot-edit',

    /**
     * Feature flag for the Bulk Renumber/Reorder upload entry point (WBPR-4487, epic WBPR-4277).
     * Gates the "Renumber lots" icon button in the auction Catalog toolbar. Release
     * concealment only — per-house entitlement is enforced server-side (WBPR-4486's 403), not here.
     * Off by default until the epic's in-progress/outcome surfaces (WBPR-4625/4626/4627) land.
     * API Key: ff_sp_bulk-renumber
     */
    BULK_RENUMBER = 'ff_sp_bulk-renumber',

    /**
     * Feature flag for the new side (rail) navigation (SP2-5372, spike SP2-5379).
     * ON = new persistent `AppNav` side rail (company + auction contexts, mobile drawer).
     * OFF = legacy navigation: top `Header` bar + auction `AuctionNav` sidebar.
     * API Key: ff_sp_side-navigation
     */
    SIDE_NAVIGATION = 'ff_sp_side-navigation',

    /**
     * Feature flag for registration deposit controls on the auction create/edit bidding step (BID-2587).
     * ON = show the Take deposits block in Registration details (create/edit flow only). Values are
     * local UI until deposit fields are wired to RHF and save payload.
     * OFF (default) = hide the block. Reload after Secret Menu changes. API Key: ff_sp_auction-deposits
     */
    AUCTION_DEPOSITS = 'ff_sp_auction-deposits',

    /**
     * Feature flag for the auction-scoped Invoices page (WBPR-4538, epic WBPR-2316).
     * Gates the "Invoices" nav entry and the `/auction/:auctionGuid/invoices` route. ON = the
     * nav rail shows Invoices in place of Winners. OFF (default) = the nav shows Winners and
     * the invoices route returns not found. The `/winners` route itself is never gated — it
     * stays reachable either way, since nothing in WBPR-4538 asks for it to be retired.
     * Reload after Secret Menu changes. API Key: ff_sp_invoices
     */
    INVOICES = 'ff_sp_invoices',

    /**
     * Feature flag for Live auction type and extra marketplace tiles on `/auction/create?step=type`.
     * ON = render the disabled Live option and selectable non-Proxibid marketplaces.
     * OFF (default) = Timed + Proxibid only; Live and extra marketplaces stay hidden unless
     * already present on the form (e.g. template edit).
     * Reload after Secret Menu changes. API Key: ff_sp_create-auction-marketplaces
     */
    CREATE_AUCTION_MARKETPLACES = 'ff_sp_create-auction-marketplaces',

    /**
     * Feature flag for the Compliance (OFAC) fraud queue (SP2-5989, epic SP2-5844).
     * Gates the Compliance fraud queue icon on Registered Bidders. Shared with Admin Portal, which
     * gates its Compliance tab and `/fraud-and-compliance/compliance` route on the same key, so the icon
     * never links to a route that redirects. OFF (default) = no Compliance icon.
     * Reload after Secret Menu changes. API Key: ff_sp_compliance_queue
     */
    COMPLIANCE_QUEUE = 'ff_sp_compliance_queue',

    /**
     * Feature flag for seller cover lots (epic SP2-3924).
     * Gates the catalog Cover lots manager and route (SP2-5567), the cover-lot toggle on lot
     * create and edit (SP2-5568), the Cover lot column on the async CSV mapper (SP2-5569), and
     * the featured-lot check on catalog and bulk delete (SP2-5570).
     * The shared delete modal reads the flag. The featured check runs only when the catalog-only
     * atom is on, so edit-lot delete does not warn. OFF hides all four and skips the cover-lots
     * GET and PUT. The mapper write is a mock until SP2-6203.
     * Reload after Secret Menu changes. API Key: ff_sp_cover-lots
     */
    COVER_LOTS = 'ff_sp_cover-lots',
}
